import { CoursePrice } from '@/app/components/CoursePrice'
import { PrimaryButton } from '@/app/components/PrimaryButton'
import { useCohortHelpers } from '@/app/hooks/useCohortHelpers'
import { useAuth } from '@/app/providers/Auth/Auth'
import { ICohort, ICourse } from '@/types/course'
import { useTranslations } from 'next-intl'
import { Controller, useFormContext } from 'react-hook-form'
import { MdArrowForward } from 'react-icons/md'
import { CoursePageFormValues } from '../../pages/CoursePage'
import { CourseBanner } from '../CourseBanner'
import { CohortSelector } from './CohortSelector'
import { ISAPaymentOptionAd } from './ISAPaymentOptionAd'

type EnrolmentCardProps = {
  activeCohorts: ICohort[]
  courseInfo: Pick<
    ICourse,
    | 'cohorts'
    | '_id'
    | 'educationProvider'
    | 'categories'
    | 'name'
    | 'url'
    | 'imageUrl'
    | 'financialTerms'
  >
  disabled?: boolean
  isLoading?: boolean
} & React.HTMLAttributes<HTMLFormElement>

export function EnrolmentCard({
  activeCohorts,
  courseInfo,
  disabled,
  isLoading,
  ...props
}: EnrolmentCardProps) {
  const t = useTranslations('CoursePage')
  const {
    formState: { isValid, isSubmitting },
    control,
    watch,
  } = useFormContext<CoursePageFormValues>()
  const user = useAuth()
  const { hasValidCost } = useCohortHelpers()
  const activeCohortsWithValidCost = activeCohorts.filter(hasValidCost)

  const selectedCohortId = watch('selectedCohortId')
  const hasActiveCohortsWithValidCost =
    Array.isArray(activeCohortsWithValidCost) &&
    activeCohortsWithValidCost.length > 0

  const { financialTerms } = courseInfo ?? {}
  const [mainFinancialTerm] = financialTerms ?? []

  return (
    <aside
      {...props}
      className={`flex flex-col overflow-clip rounded-3xl border border-lines ${props.className ?? ''}`}
    >
      <CourseBanner courseInfo={courseInfo} />
      <div className="flex flex-col gap-4 p-6">
        {hasActiveCohortsWithValidCost ? (
          <div className="flex flex-col justify-between">
            <CoursePrice
              course={courseInfo}
              selectedCohortId={selectedCohortId}
              className="font-semibold leading-8 tracking-tight"
              amountProps={{ className: '!text-3xl flex-1' }}
            />
            {activeCohortsWithValidCost.length > 0 && (
              <Controller
                control={control}
                name="selectedCohortId"
                render={({ field }) => (
                  <CohortSelector
                    className="mt-5"
                    cohorts={activeCohortsWithValidCost}
                    {...field}
                  />
                )}
              />
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-xl font-semibold text-[--text]">
              {t('new_cohorts_coming_soon')}
            </p>
          </div>
        )}
        <hr />

        <ISAPaymentOptionAd financialTerm={mainFinancialTerm}>
          <PrimaryButton
            type="submit"
            id={`enrolment-card-button--${user?.isAuthenticated ? 'redirect-to-checkout' : 'redirect-to-signup'}`}
            disabled={
              user.isLoading ||
              !hasActiveCohortsWithValidCost ||
              ((disabled || isLoading || !isValid) && user?.isAuthenticated)
            }
            isLoading={isSubmitting}
          >
            {user?.isAuthenticated && !isLoading ? (
              <div className="flex items-center gap-2">
                <p className="ml-auto">{t('continue')}</p>
                <MdArrowForward className="ml-auto h-6 w-6" />
              </div>
            ) : (
              t('sign_up_to_apply')
            )}
          </PrimaryButton>
        </ISAPaymentOptionAd>
      </div>
    </aside>
  )
}
